import { API_HOST } from '../config';

/**
 * Product feedback: what people want changed in the dashboard.
 *
 * The four endpoints are live (verified 26 Oct 2026 on team.influencer.in,
 * which resolves to the same hosts as api.influencer.in). Note the paths
 * carry no trailing slash -- `/api/v1/feedback/` is a 404 -- so do not add
 * one. The shape is written down in docs/superpowers/specs; this file and
 * that doc have to agree.
 */

const BASE_URL = `${API_HOST}/api/v1/feedback`;

function authHeaders(extra = {}) {
  return {
    Authorization: `Bearer ${localStorage.getItem('access')}`,
    ...extra,
  };
}

async function parseError(response) {
  let payload = {};
  try {
    payload = await response.json();
  } catch (e) {
    payload = {};
  }
  // DRF permission refusals carry their text in `detail`, not `message`.
  const error = new Error(
    payload.message || payload.detail || 'Something went wrong. Please try again.',
  );
  error.status = response.status;
  error.fieldErrors = payload;
  return error;
}

async function request(url, options = {}) {
  const response = await fetch(url, options);
  if (!response.ok) {
    throw await parseError(response);
  }
  if (response.status === 204) {
    return null;
  }
  return response.json();
}

/** A list response may be bare or paginated; callers want the rows. */
function rows(payload) {
  if (Array.isArray(payload)) return payload;
  if (payload && Array.isArray(payload.results)) return payload.results;
  return [];
}

/**
 * Every failure reaches the screen, including a 404.
 *
 * While the endpoints were still being built a 404 meant "no API yet" and
 * was swallowed into an empty list. Now that they are live it means the
 * route moved, and an empty board that is really a broken one is the worst
 * of the two outcomes -- it reads as "nobody has asked for anything",
 * which is a lie the board exists to stop telling.
 */
function list(url) {
  return request(url, { headers: authHeaders() }).then(rows);
}

/** Everything this person has raised, newest first. */
export function getMyFeedback() {
  return list(`${BASE_URL}/mine`);
}

/** The whole board. Staff only -- the API enforces it, the route repeats it. */
export function getFeedbackBoard() {
  return list(BASE_URL);
}

/**
 * Raise a new item. Returns the stored row, including its id and state.
 *
 * Sends JSON when there is nothing attached and multipart when there is,
 * which is what DRF's default parsers accept on one view. Going multipart
 * for every report would turn every field into a string and lose the
 * typing for the sake of the minority of reports that carry an image.
 */
export function createFeedback(payload, files) {
  const images = Array.isArray(files) ? files.filter(Boolean) : [];

  if (images.length === 0) {
    return request(BASE_URL, {
      method: 'POST',
      headers: authHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(payload),
    });
  }

  const formData = new FormData();
  Object.keys(payload || {}).forEach((key) => {
    const value = payload[key];
    if (value !== undefined && value !== null) formData.append(key, value);
  });
  images.forEach((image) => formData.append('screenshots', image.file, image.name));

  // Content-Type is deliberately omitted so the browser sets the
  // multipart boundary itself.
  return request(BASE_URL, {
    method: 'POST',
    headers: authHeaders(),
    body: formData,
  });
}

/**
 * Triage: state, owner, reply, a corrected impact, or what it duplicates.
 *
 * Deliberately one endpoint rather than a verb per transition. The allowed
 * moves are decided in feedbackTypes.js and enforced again server-side;
 * splitting them into /plan, /ship, /decline would put that rule in three
 * places and keep it in none.
 */
export function updateFeedback(id, changes) {
  return request(`${BASE_URL}/${id}`, {
    method: 'PATCH',
    headers: authHeaders({ 'Content-Type': 'application/json' }),
    body: JSON.stringify(changes),
  });
}
